import { Link } from 'react-router-dom'
import { useProducts, useProductInventory } from '../../hooks/useProducts'
import { formatMoney, emojiForProduct, colorPairForProduct } from '../../lib/format'
import type { Product } from '../../lib/database.types'
import { Skeleton } from '../../components/ui'

export default function ProductsList() {
  const { data: products, isLoading } = useProducts()

  if (isLoading) return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
        <Skeleton width={110} height={32} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} height={120} />
        ))}
      </div>
    </div>
  )

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 'var(--space-4)' }}>
        <Link
          to="/prints/products/new"
          style={{
            padding: 'var(--space-2) var(--space-4)',
            background: 'var(--color-primary)',
            color: 'white',
            borderRadius: 'var(--radius-md)',
            fontWeight: 600,
            fontSize: 'var(--font-size-sm)',
            textDecoration: 'none',
          }}
        >
          + Add product
        </Link>
      </div>

      {!products || products.length === 0 ? (
        <div
          style={{
            padding: 'var(--space-8)',
            textAlign: 'center',
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            color: 'var(--color-text-muted)',
          }}
        >
          No products yet — tap &quot;Add product&quot; to create your first catalog item.
        </div>
      ) : (
        <div
          style={{
            maxHeight: '70vh',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-3)',
            background: 'var(--color-bg)',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 'var(--space-2)',
            }}
          >
            {products.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

function ProductCard({ product }: { product: Product }) {
  const inv = useProductInventory(product.id)
  const stockColor =
    inv.inStock === 0
      ? 'var(--color-text-faint)'
      : inv.inStock <= 2
      ? 'var(--color-warning)'
      : 'var(--color-success)'

  const priceLabel =
    inv.avgUnitPrice > 0
      ? `${formatMoney(inv.avgUnitPrice)} avg`
      : product.default_unit_price != null
      ? formatMoney(Number(product.default_unit_price))
      : '—'

  const [c1, c2] = colorPairForProduct(product.name)

  return (
    <Link
      to={`/prints/products/${product.id}`}
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-2)',
        textDecoration: 'none',
        color: 'var(--color-text)',
        border: '1px solid var(--color-border)',
        minWidth: 0,
        display: 'block',
      }}
      title={`${product.name} · ${inv.inStock} in stock · ${priceLabel}`}
    >
      <div
        style={{
          fontSize: '2rem',
          textAlign: 'center',
          background: `linear-gradient(135deg, ${c1}, ${c2})`,
          borderRadius: 'var(--radius-sm)',
          padding: 'var(--space-2)',
          marginBottom: 'var(--space-2)',
          lineHeight: 1.1,
        }}
      >
        {emojiForProduct(product.name)}
      </div>
      <div
        style={{
          fontWeight: 600,
          fontSize: 'var(--font-size-xs)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {product.name}
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginTop: 4,
          fontSize: 10,
          gap: 4,
        }}
      >
        <span style={{ color: stockColor, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
          {inv.inStock} stock
        </span>
        <span style={{ color: 'var(--color-text-faint)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {priceLabel}
        </span>
      </div>
    </Link>
  )
}
